import { useCallback, useRef, useState } from "react";
import {
  getEmployeeDevice,
  getWorkforceDevice,
  createAMessage,
  getMyMessages,
} from "../../../utils";
import { connectionFromDevice, isCurrentLocation, mergeDeviceState, readFetchedDevice } from "../devicePayload";
import { useSelector } from "react-redux";

export const useDeviceTracker = () => {
  const token = sessionStorage.getItem("token");
  const { role } = useSelector((state) => state.userState);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastConexion, setLastConexion] = useState(null);

  const [device, setDevice] = useState(undefined);
  const deviceRequestId = useRef(0);
  const droppedDeviceUids = useRef(new Set());
  const locationBaselineAt = useRef(undefined);

  const [loading, setLoading] = useState(false);
  const [uploadingMessage, setUploadingMessage] = useState(false);
  const [sendingTextMessage, setSendingTextMessage] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [textMessages, setTextMessages] = useState([]);
  const [loadingTextMessages, setLoadingTextMessages] = useState(false);

  const handleNewMessageSocket = useCallback((newMessage) => {
    setTextMessages((prev) => {
      const exists = prev.some(
        (msg) =>
          msg.uid === newMessage.uid ||
          (msg.createdAt === newMessage.createdAt &&
            msg.senderUid === newMessage.senderUid)
      );
      return exists ? prev : [...prev, newMessage];
    });
  }, []);

  const getDeviceById = useCallback(
    async (userUUID, isWorkforce = false) => {
      const requestId = ++deviceRequestId.current;

      try {
        setLoading(true);

        const res = isWorkforce
          ? await getWorkforceDevice(token, userUUID)
          : await getEmployeeDevice(token, userUUID);

        if (requestId !== deviceRequestId.current) return;

        const deviceData = readFetchedDevice(res);
        if (deviceData?.uid && droppedDeviceUids.current.has(deviceData.uid)) {
          setDevice(null);
          setLastConexion(null);
          return;
        }

        if (!deviceData) {
          setLastConexion(null);
          locationBaselineAt.current = undefined;
        } else {
          const point = connectionFromDevice(deviceData);
          const baseline = locationBaselineAt.current;
          if (point && baseline === undefined) {
            locationBaselineAt.current = point;
            if (!Number.isFinite(point.at) || isCurrentLocation(point)) {
              setLastConexion({ lat: point.lat, lng: point.lng });
            }
          } else if (point && baseline) {
            const newer = Number.isFinite(point.at) && (!Number.isFinite(baseline.at) || point.at > baseline.at);
            const moved = point.lat !== baseline.lat || point.lng !== baseline.lng;
            if (newer || moved) {
              locationBaselineAt.current = point;
              setLastConexion({ lat: point.lat, lng: point.lng });
            }
          }
        }

        setDevice((current) => mergeDeviceState(current, deviceData));
      } catch (error) {
        if (requestId !== deviceRequestId.current) return;
      } finally {
        if (requestId === deviceRequestId.current) setLoading(false);
      }
    },
    [token]
  );

  const dropDevice = useCallback((deviceUid) => {
    if (deviceUid) droppedDeviceUids.current.add(deviceUid);
    setDevice(null);
    setLastConexion(null);
    locationBaselineAt.current = undefined;
  }, []);

  /**
   * Messages
   */
  const uploadMediaRecording = useCallback(
    async (audioBlob, recordingTime, recipientUid, companyId) => {
      setUploadingMessage(true);
      try {
        const audioFile = new File([audioBlob], "recording.webm", {
          type: "video/webm; codecs=opus",
        });

        const res = await createAMessage(token, {
          file: audioFile,
          name: `Voice Message - ${new Date().toLocaleString()}`,
          receiver: recipientUid,
          companyId,
          currentUserRole: role,
          receiverInfo: {},
        });

        setTextMessages((prev) => [...prev, res.data]);
      } finally {
        setUploadingMessage(false);
      }
    },
    [token, role]
  );

  const sendTextMessage = useCallback(
    async (message, receiverUid, companyId) => {
      setSendingTextMessage(true);
      try {
        const res = await createAMessage(token, {
          plainText: message,
          name: `Text Message - ${new Date().toLocaleString()}`,
          receiver: receiverUid,
          companyId,
          currentUserRole: role,
          receiverInfo: {},
        });

        setTextMessages((prev) => [...prev, res.data]);
      } finally {
        setSendingTextMessage(false);
      }
    },
    [token, role]
  );

  return {
    getDeviceById,
    uploadMediaRecording,
    sendTextMessage,
    handleNewMessageSocket,

    setDevice,
    dropDevice,
    device,
    lastConexion,
    setLastConexion,
    loading,

    uploadingMessage,
    sendingTextMessage,
    textMessages,
    loadingTextMessages,
    hasMore,
  };
};
